import {
  type FormEvent,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  GripVertical,
  Loader2,
  Plus,
  Trash2,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { TAG_COLORS, TAG_COLOR_KEYS } from "@/lib/constants";
import { cn } from "@/lib/utils";
import { getProject, updateProject } from "@/api/projects";
import { getTaskStatuses } from "@/api/tasks";
import { useProjectStore } from "@/stores/project.store";
import { usePermissions } from "@/hooks/usePermissions";
import type { Project, TaskStatusConfig } from "@/api/types";

interface TaskStatusesSectionProps {
  project: Project;
}

interface DraftStatus extends TaskStatusConfig {
  /** Local-only stable id for dnd-kit (server `key` collides for
   *  newly-added rows that haven't been saved yet). */
  _id: string;
}

/** Default fallback when the project has no resolved catalog yet. */
const SEED_STATUSES: TaskStatusConfig[] = [
  {
    key: "todo",
    label: "To do",
    color: "gray",
    order: 0,
    is_initial: true,
    is_done: false,
  },
  {
    key: "done",
    label: "Done",
    color: "emerald",
    order: 1,
    is_initial: false,
    is_done: true,
  },
];

/** Pick the best locally-known status list while the live fetch
 *  is in flight. effective_task_statuses is the server-resolved
 *  view (overrides merged over project_type defaults); falling
 *  back to project.config preserves an in-progress edit if the
 *  project is fresh from a save and the API is briefly behind. */
function localTaskStatuses(project: Project): TaskStatusConfig[] {
  if (project.effective_task_statuses?.length) {
    return project.effective_task_statuses;
  }
  const cfg = project.config?.task_statuses;
  if (Array.isArray(cfg) && cfg.length > 0) {
    return cfg as TaskStatusConfig[];
  }
  return SEED_STATUSES;
}

export function TaskStatusesSection({ project }: TaskStatusesSectionProps) {
  const canEdit = usePermissions().canEditProject;
  const setCurrentProject = useProjectStore((s) => s.setCurrentProject);
  const setProjects = useProjectStore((s) => s.setProjects);
  const projects = useProjectStore((s) => s.projects);

  // Source of truth: GET /projects/{slug}/tasks/statuses, which
  // resolves project.config.task_statuses → project_type.features
  // .task_statuses → DEFAULT_TASK_STATUSES on the server. Reading
  // project.effective_task_statuses alone wasn't enough — when the
  // project hasn't shipped an override and the field is empty,
  // we'd fall through to a 2-row local seed (todo + done), even
  // though the project type ships the full kanban catalog
  // (in_progress / in_review / etc.).
  const [fetched, setFetched] = useState<TaskStatusConfig[] | null>(null);
  useEffect(() => {
    let cancelled = false;
    getTaskStatuses(project.slug)
      .then((s) => {
        if (!cancelled) setFetched(s);
      })
      .catch(() => {
        // Silent — the local fallback below keeps the UI usable.
      });
    return () => {
      cancelled = true;
    };
  }, [project.slug]);

  const initial = useMemo<DraftStatus[]>(() => {
    const source = fetched ?? localTaskStatuses(project);
    return source
      .slice()
      .sort((a, b) => a.order - b.order)
      .map((s) => ({ ...s, _id: s.key }));
  }, [fetched, project]);

  const [drafts, setDrafts] = useState<DraftStatus[]>(initial);
  const [saving, setSaving] = useState(false);

  // Resync drafts when the *content* of the resolved list changes
  // (after a save, project switch, or out-of-band refresh). A bare
  // reference dep would also fire on every store touch, wiping
  // mid-edit state.
  const initialKey = useMemo(
    () =>
      JSON.stringify(
        initial.map((s) => ({
          key: s.key,
          label: s.label,
          color: s.color ?? "gray",
          is_initial: !!s.is_initial,
          is_done: !!s.is_done,
        })),
      ),
    [initial],
  );
  useEffect(() => {
    setDrafts(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialKey]);

  const isDirty = useMemo(() => {
    if (drafts.length !== initial.length) return true;
    for (let i = 0; i < drafts.length; i += 1) {
      const a = drafts[i];
      const b = initial[i];
      if (
        a.key !== b.key ||
        a.label !== b.label ||
        (a.color ?? "gray") !== (b.color ?? "gray") ||
        !!a.is_initial !== !!b.is_initial ||
        !!a.is_done !== !!b.is_done
      ) {
        return true;
      }
    }
    return false;
  }, [drafts, initial]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  function handleDragEnd(event: DragEndEvent) {
    if (!canEdit) return;
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = drafts.findIndex((s) => s._id === active.id);
    const newIndex = drafts.findIndex((s) => s._id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    setDrafts(arrayMove(drafts, oldIndex, newIndex));
  }

  function patchRow(_id: string, patch: Partial<DraftStatus>) {
    setDrafts((prev) =>
      prev.map((s) => (s._id === _id ? { ...s, ...patch } : s)),
    );
  }

  function setInitial(_id: string) {
    // is_initial is mutually exclusive: setting one clears all others.
    setDrafts((prev) =>
      prev.map((s) => ({ ...s, is_initial: s._id === _id })),
    );
  }

  function toggleDone(_id: string, on: boolean) {
    patchRow(_id, { is_done: on });
  }

  function deleteRow(_id: string) {
    setDrafts((prev) => {
      const target = prev.find((s) => s._id === _id);
      if (!target) return prev;
      // Don't let the user remove the only is_initial row — there'd be
      // no default for new tasks. Block softly.
      if (
        target.is_initial &&
        prev.filter((s) => s.is_initial).length <= 1
      ) {
        toast.error("Pick another default before removing this one.");
        return prev;
      }
      return prev.filter((s) => s._id !== _id);
    });
  }

  function addStatus(label: string, color: string) {
    const trimmed = label.trim();
    if (!trimmed) return;
    const baseKey = slugify(trimmed);
    if (!baseKey) {
      toast.error("Pick a label with at least one letter or digit.");
      return;
    }
    let key = baseKey;
    let i = 2;
    const taken = new Set(drafts.map((s) => s.key));
    while (taken.has(key)) key = `${baseKey}_${i++}`;
    const order = drafts.length;
    setDrafts((prev) => [
      ...prev,
      {
        _id: `__new_${Date.now()}_${order}`,
        key,
        label: trimmed,
        color,
        order,
        is_initial: prev.length === 0, // first row becomes default
        is_done: false,
      },
    ]);
  }

  function reset() {
    setDrafts(initial);
  }

  async function save() {
    setSaving(true);
    try {
      const payload: TaskStatusConfig[] = drafts.map((s, i) => ({
        key: s.key,
        label: s.label.trim(),
        order: i,
        color: s.color ?? "gray",
        is_initial: !!s.is_initial,
        is_done: !!s.is_done,
      }));
      const invalid = payload.find((s) => s.label.length === 0);
      if (invalid) {
        toast.error("Every status needs a label.");
        return;
      }
      const initials = payload.filter((s) => s.is_initial);
      if (initials.length === 0) {
        toast.error("Pick a default status (one row marked Default).");
        return;
      }
      if (initials.length > 1) {
        toast.error("Only one status can be the default.");
        return;
      }
      await updateProject(project.slug, { task_statuses: payload });
      // Refetch so the rest of the app picks up the canonical
      // effective_task_statuses.
      const updated = await getProject(project.slug);
      setCurrentProject(updated);
      setProjects(
        projects.map((p) => (p.slug === updated.slug ? updated : p)),
      );
      toast.success("Task statuses updated");
    } catch (err) {
      toast.error(extractMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <section className="space-y-5">
      <header className="flex items-end justify-between gap-3">
        <div>
          <h2 className="text-lg font-medium tracking-tight text-slate-900">
            Task statuses
          </h2>
          <p className="text-sm text-slate-500">
            Columns of the Tasks kanban board. Mark one as Default for new
            tasks; mark any as Completed to count toward "done".
          </p>
        </div>
        {canEdit && isDirty && (
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={reset}
              disabled={saving}
            >
              Discard
            </Button>
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={() => void save()}
              disabled={saving || drafts.length === 0}
            >
              {saving && <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />}
              Save changes
            </Button>
          </div>
        )}
      </header>

      <div className="rounded-plane bg-white p-2 shadow-plane">
        {drafts.length === 0 ? (
          <p className="px-4 py-6 text-sm text-slate-400">
            No task statuses, add one below.
          </p>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={drafts.map((s) => s._id)}
              strategy={verticalListSortingStrategy}
            >
              <ul className="divide-y divide-slate-900/[0.06]">
                {drafts.map((s) => (
                  <SortableRow
                    key={s._id}
                    status={s}
                    canEdit={canEdit}
                    canDelete={drafts.length > 1}
                    onLabelChange={(v) => patchRow(s._id, { label: v })}
                    onColorChange={(c) => patchRow(s._id, { color: c })}
                    onSetInitial={() => setInitial(s._id)}
                    onToggleDone={(on) => toggleDone(s._id, on)}
                    onDelete={() => deleteRow(s._id)}
                  />
                ))}
              </ul>
            </SortableContext>
          </DndContext>
        )}

        {canEdit && <AddStatusInline onAdd={addStatus} />}
      </div>
    </section>
  );
}

// ─── Single sortable row ────────────────────────────────────────────────────

interface SortableRowProps {
  status: DraftStatus;
  canEdit: boolean;
  canDelete: boolean;
  onLabelChange: (value: string) => void;
  onColorChange: (color: string) => void;
  onSetInitial: () => void;
  onToggleDone: (on: boolean) => void;
  onDelete: () => void;
}

function SortableRow({
  status,
  canEdit,
  canDelete,
  onLabelChange,
  onColorChange,
  onSetInitial,
  onToggleDone,
  onDelete,
}: SortableRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: status._id });
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : "auto",
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={cn(
        "group flex items-center gap-3 rounded-md px-2 py-2 transition-shadow",
        isDragging
          ? "bg-white shadow-card-open ring-1 ring-indigo-200"
          : "hover:bg-slate-50",
      )}
    >
      <button
        type="button"
        aria-label={`Reorder ${status.label}`}
        className={cn(
          "shrink-0 cursor-grab rounded p-1 text-slate-400 hover:text-slate-600 active:cursor-grabbing",
          !canEdit && "pointer-events-none opacity-30",
        )}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" />
      </button>

      <ColorDotPicker
        color={status.color ?? "gray"}
        canEdit={canEdit}
        onPick={onColorChange}
      />

      <Input
        value={status.label}
        onChange={(e) => onLabelChange(e.target.value)}
        disabled={!canEdit}
        // pointerdown stop-propagation prevents the dnd-kit sensor from
        // claiming the input's click as a drag start.
        onPointerDown={(e) => e.stopPropagation()}
        className="h-8 flex-1 border-transparent text-sm shadow-none focus-visible:border-slate-200 focus-visible:bg-white"
        placeholder="Label"
      />

      <label
        className={cn(
          "inline-flex shrink-0 items-center gap-1.5 text-xs",
          canEdit ? "cursor-pointer" : "cursor-default",
        )}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <input
          type="radio"
          name="task-status-initial"
          checked={!!status.is_initial}
          onChange={() => onSetInitial()}
          disabled={!canEdit}
          className="h-3.5 w-3.5 cursor-pointer accent-indigo-600 disabled:cursor-default"
        />
        <span className="text-slate-600">Default</span>
      </label>

      <label
        className={cn(
          "inline-flex shrink-0 items-center gap-1.5 text-xs",
          canEdit ? "cursor-pointer" : "cursor-default",
        )}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <Checkbox
          checked={!!status.is_done}
          disabled={!canEdit}
          onCheckedChange={(v) => onToggleDone(v === true)}
        />
        <span className="text-slate-600">Completed</span>
      </label>

      {canEdit && canDelete && (
        <button
          type="button"
          onClick={onDelete}
          aria-label={`Delete ${status.label}`}
          className="shrink-0 rounded p-1 text-rose-400/40 opacity-0 transition-opacity hover:bg-rose-50 hover:text-rose-600 focus-visible:opacity-100 group-hover:opacity-100"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      )}
    </li>
  );
}

// ─── Color picker ──────────────────────────────────────────────────────────

interface ColorDotPickerProps {
  color: string;
  canEdit: boolean;
  onPick: (color: string) => void;
}

function ColorDotPicker({ color, canEdit, onPick }: ColorDotPickerProps) {
  const [open, setOpen] = useState(false);
  const colors = TAG_COLORS[color] ?? TAG_COLORS.gray;
  if (!canEdit) {
    return (
      <span
        className={cn(
          "h-4 w-4 shrink-0 rounded-full ring-1 ring-inset ring-black/5",
          colors.swatch,
        )}
      />
    );
  }
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Change color"
          onPointerDown={(e) => e.stopPropagation()}
          className={cn(
            "h-4 w-4 shrink-0 cursor-pointer rounded-full ring-1 ring-inset ring-black/5 transition-all",
            "hover:scale-110 hover:ring-2 hover:ring-slate-300 hover:ring-offset-1",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400 focus-visible:ring-offset-1",
            colors.swatch,
          )}
        />
      </PopoverTrigger>
      <PopoverContent align="start" sideOffset={6} className="w-auto p-3">
        <div className="grid grid-cols-9 gap-1.5">
          {TAG_COLOR_KEYS.map((c) => {
            const cs = TAG_COLORS[c];
            const isSelected = c === color;
            return (
              <button
                key={c}
                type="button"
                aria-label={c}
                onClick={() => {
                  onPick(c);
                  setOpen(false);
                }}
                className={cn(
                  "h-6 w-6 rounded-full transition-transform hover:scale-110",
                  cs.swatch,
                  isSelected && "ring-2 ring-slate-900 ring-offset-2",
                )}
              />
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ─── Inline add row ────────────────────────────────────────────────────────

interface AddStatusInlineProps {
  onAdd: (label: string, color: string) => void;
}

function AddStatusInline({ onAdd }: AddStatusInlineProps) {
  const [label, setLabel] = useState("");
  const [color, setColor] = useState<string>("gray");

  function submit(e?: FormEvent) {
    e?.preventDefault();
    if (!label.trim()) return;
    onAdd(label, color);
    setLabel("");
    setColor("gray");
  }

  return (
    <form
      onSubmit={submit}
      className="flex items-center gap-3 border-t border-slate-900/[0.06] px-2 pt-3"
    >
      <Plus className="h-4 w-4 text-slate-300" aria-hidden />
      <ColorDotPicker color={color} canEdit onPick={setColor} />
      <Input
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        placeholder="Add a status…"
        className="h-8 flex-1 border-transparent text-sm shadow-none focus-visible:border-slate-200 focus-visible:bg-white"
      />
      <Button
        type="submit"
        size="sm"
        variant="outline"
        disabled={!label.trim()}
      >
        Add
      </Button>
    </form>
  );
}

// ─── Helpers ───────────────────────────────────────────────────────────────

function slugify(label: string): string {
  return label
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
}

function extractMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const detail = (err.response?.data as { detail?: unknown } | undefined)
      ?.detail;
    if (typeof detail === "string") return detail;
  }
  return "Couldn't save the task statuses.";
}
